import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    // Find the closest section or dialog (standard Shopify structure)
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    // 1. Safety check for event data
    if (!event.detail || !event.detail.data) return;

    // 2. Handle Product ID filtering
    // If we are on a collection page with multiple products, we need to ensure we only
    // update the price for the specific product that triggered the event.
    if (this.dataset.productId) {
       // If the event suggests a new product (e.g., swatches), update our ID
      if (event.detail.data.newProduct) {
        this.dataset.productId = event.detail.data.newProduct.id;
      } 
      // If the event source has a product ID and it doesn't match ours, ignore it.
      else if (
        event.target instanceof HTMLElement && 
        event.target.dataset.productId && 
        event.target.dataset.productId !== this.dataset.productId
      ) {
        return;
      }
    }

    // 3. Get the HTML from the event. 
    // Sometimes it is a string, sometimes it is a DOM Document. We handle both.
    let responseHtml = event.detail.data.html;
    
    // If it's a string, parse it into a DOM object
    if (typeof responseHtml === 'string') {
      responseHtml = new DOMParser().parseFromString(responseHtml, 'text/html');
    }

    // 4. Find the new price container in the response HTML
    // We try to be specific first (matching ID), then fall back to a generic query.
    let newPriceSource;
    
    if (this.dataset.productId) {
        // Try to find the specific product-price for this ID in the new HTML
        const matchingParent = responseHtml.querySelector(`product-price[data-product-id="${this.dataset.productId}"]`);
        if (matchingParent) {
            newPriceSource = matchingParent.querySelector('[ref="priceContainer"]');
        }
    }

    // Fallback: If we couldn't find a specific ID match, look for ANY product-price container 
    // (This covers cases where the ID might be missing in the response)
    if (!newPriceSource) {
        newPriceSource = responseHtml.querySelector('product-price [ref="priceContainer"]');
    }

    const currentPrice = this.querySelector('[ref="priceContainer"]');

    // 5. If we have both the new and old elements, swap the content
    if (!newPriceSource || !currentPrice) return;

    if (currentPrice.innerHTML !== newPriceSource.innerHTML) {
      currentPrice.innerHTML = newPriceSource.innerHTML;
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}